import { useState } from "react";
import { Plus, X } from "lucide-react";

const chipBaseCls =
  "px-3.5 py-1.5 rounded-full text-sm font-medium ring-1 transition-colors";
const activeCls = "bg-[#3FFFB6] text-[#111827] ring-[#3FFFB6]";
const idleCls =
  "bg-white text-[#4B5563] ring-[#F3F4F6] hover:ring-[#B8FFDD] hover:text-[#111827]";

// A single labelled row of chips. Optional custom-submit input via `onSubmit`.
export function ChipGroup({
  label,
  options,
  active,
  onToggle,
  testIdPrefix,
  onSubmit, // (value: string) => void — if provided, renders a "+ Submit …" affordance
  submitLabel,
}) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");

  const commit = () => {
    const v = draft.trim();
    if (!v) return;
    onSubmit?.(v);
    setDraft("");
    setAdding(false);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {label && (
        <span className="text-xs font-semibold uppercase tracking-wider text-[#9CA3AF] mr-1">
          {label}
        </span>
      )}
      {options.map((opt) => {
        const isActive = active.includes(opt);
        return (
          <button
            key={opt}
            data-testid={`${testIdPrefix}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
            onClick={() => onToggle(opt)}
            className={`${chipBaseCls} ${isActive ? activeCls : idleCls}`}
          >
            {opt}
          </button>
        );
      })}
      {onSubmit && (adding ? (
        <span className="inline-flex items-center gap-1 bg-white ring-1 ring-[#3FFFB6] rounded-full pl-3 pr-1 h-9">
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              else if (e.key === "Escape") { setAdding(false); setDraft(""); }
            }}
            placeholder={submitLabel || "Type and press enter"}
            data-testid={`${testIdPrefix}-submit-input`}
            className="text-sm h-7 outline-none min-w-48 placeholder:text-[#9CA3AF]"
          />
          <button
            type="button"
            onClick={commit}
            data-testid={`${testIdPrefix}-submit-confirm`}
            className="w-7 h-7 rounded-full bg-[#3FFFB6] hover:bg-[#2DE59F] text-[#111827] inline-flex items-center justify-center"
            aria-label="Add"
          >
            <Plus size={14} />
          </button>
          <button
            type="button"
            onClick={() => { setAdding(false); setDraft(""); }}
            className="w-7 h-7 rounded-full text-[#9CA3AF] hover:text-[#111827] inline-flex items-center justify-center"
            aria-label="Cancel"
          >
            <X size={14} />
          </button>
        </span>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          data-testid={`${testIdPrefix}-submit-open`}
          className={`${chipBaseCls} bg-white text-[#3FFFB6] ring-dashed ring-[#B8FFDD] hover:ring-[#3FFFB6] inline-flex items-center gap-1`}
        >
          <Plus size={12} />
          {submitLabel || `Submit ${label ? label.toLowerCase() : "custom"}`}
        </button>
      ))}
    </div>
  );
}

// Gated filter: user picks WHICH facet they want to filter by (Category vs
// AI Model vs Software Tool). We only reveal the pill options once they pick a
// lane. Keeps the hero calm instead of throwing many chips at first render.
export default function FilterChips({
  categories,
  tools, // AI Models list
  softwareTools = [],
  activeCategories,
  activeTools, // active AI Models
  activeSoftwareTools = [],
  onToggleCategory,
  onToggleTool, // toggle AI Model
  onToggleSoftwareTool = () => {},
  onClear,
  onSubmitCategory,
  onSubmitTool, // submit custom AI Model
  onSubmitSoftwareTool,
}) {
  const [lane, setLane] = useState(null); // null | "category" | "product" | "software"
  const anyActive =
    activeCategories.length + activeTools.length + activeSoftwareTools.length > 0;

  const LaneButton = ({ id, label, count, testId }) => {
    const active = lane === id;
    return (
      <button
        type="button"
        onClick={() => setLane(active ? null : id)}
        data-testid={testId}
        className={`inline-flex items-center gap-2 px-4 h-10 rounded-full text-sm font-semibold ring-1 transition-colors ${
          active
            ? "bg-[#111827] text-white ring-[#111827]"
            : "bg-white text-[#111827] ring-[#E5E7EB] hover:ring-[#111827]"
        }`}
      >
        {label}
        {count > 0 && (
          <span
            className={`text-[10px] rounded-full px-1.5 py-0.5 ${
              active ? "bg-white/20 text-white" : "bg-[#D6FFEB] text-[#3FFFB6]"
            }`}
          >
            {count}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="w-full flex flex-col gap-4">
      <div className="flex items-center gap-2 flex-wrap">
        <LaneButton
          id="category"
          label="Filter by Category"
          count={activeCategories.length}
          testId="lane-category"
        />
        <LaneButton
          id="product"
          label="Filter by AI Model"
          count={activeTools.length}
          testId="lane-product"
        />
        <LaneButton
          id="software"
          label="Filter by Software Tool"
          count={activeSoftwareTools.length}
          testId="lane-software"
        />
        {anyActive && (
          <button
            data-testid="clear-filters"
            onClick={() => {
              onClear();
              setLane(null);
            }}
            className="ml-auto text-sm font-medium text-[#3FFFB6] hover:text-[#2DE59F] transition-colors"
          >
            Clear all filters
          </button>
        )}
      </div>

      {lane === "category" && (
        <ChipGroup
          options={categories}
          active={activeCategories}
          onToggle={onToggleCategory}
          testIdPrefix="filter-category"
          onSubmit={onSubmitCategory}
          submitLabel="Submit a category"
        />
      )}
      {lane === "product" && (
        <ChipGroup
          options={tools}
          active={activeTools}
          onToggle={onToggleTool}
          testIdPrefix="filter-tool"
          onSubmit={onSubmitTool}
          submitLabel="Submit an AI Model"
        />
      )}
      {lane === "software" && (
        <ChipGroup
          options={softwareTools}
          active={activeSoftwareTools}
          onToggle={onToggleSoftwareTool}
          testIdPrefix="filter-software"
          onSubmit={onSubmitSoftwareTool}
          submitLabel="Submit a software tool"
        />
      )}
    </div>
  );
}
